import { ArrowDownTrayIcon, ArrowPathIcon, CheckIcon, TrashIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React, { useState } from "react";

import { Button, Dialog, Label, Paragraph } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import {
  ApiVideoSessionSessionStatusChoices,
  useManuallyMarkMediaAsViewedMutation,
  useRemoveSessionDataMutation,
} from "@api/graphql/types-and-hooks";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { notifySuccess } from "@components/notifications/notifications";
import { FeedTooltip } from "@pages/VideoSessionsV2/components/Media/FeedTooltip";
import { MediaCardType } from "@pages/VideoSessionsV2/components/MediaCards";
import { useDownloadTranscriptCSV } from "@pages/VideoSessionsV2/components/Utils/helper";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface FeedMediaCardHeaderProps {
  learnerFullName: string;
  providerFirstName?: string;
  providerLastName?: string;
  date: string;
  sessionRating?: number;
  media?: MediaCardType;
  isMissingMedia?: boolean;
}

export const FeedMediaCardHeader: React.FC<FeedMediaCardHeaderProps> = ({
  learnerFullName,
  providerFirstName,
  providerLastName,
  date,
  media,
  sessionRating,
  isMissingMedia,
}) => {
  const { roles } = useAdminData();

  const { setViewedMediaMap, setDeletedMediaIds } = useVideoSessionData();
  const [isDeleteMediaDialogOpen, setIsDeleteMediaDialogOpen] = useState<boolean>(false);
  const { downloadTranscriptCSV, isLoading: isTranscriptDownloading } = useDownloadTranscriptCSV();
  const markAsReviewedMutation = useManuallyMarkMediaAsViewedMutation({
    onSuccess: async (data) => {
      const updatedMedia = data.manuallyMarkMediaAsViewed?.media;

      if (media) {
        setViewedMediaMap((prev) => ({ ...prev, [media.id]: updatedMedia ? updatedMedia.isViewed : media.isViewed }));
      }
    },
  });
  const { isPending } = markAsReviewedMutation;

  const deleteMediaSessionMutation = useRemoveSessionDataMutation({
    onSuccess: async () => {
      notifySuccess("Video deleted successfully!");
      if (media) {
        setDeletedMediaIds((prev) => new Set(prev).add(media.id));
      }
    },
  });

  const { isPending: isMediaDeleting } = deleteMediaSessionMutation;

  const handleMarkAsReviewed = async (isReviewed: boolean) => {
    await markAsReviewedMutation.mutateAsync({
      mediId: media ? media?.id : "",
      isReviewed,
    });
  };

  const handleDeleteMedia = async () => {
    await deleteMediaSessionMutation.mutateAsync({
      input: {
        videoSessionId: media?.videoSession?.id || "",
      },
    });

    setIsDeleteMediaDialogOpen(false);
  };
  const menuItems: PopoverMenuItem[] = [
    {
      label: !media?.isViewed ? "Mark as Reviewed" : "Mark as Unreviewed",
      Icon: CheckIcon,
      callback: () => {
        if (media) {
          handleMarkAsReviewed(!media.isViewed);
        }
        return;
      },
    },
    {
      label: isTranscriptDownloading ? "Downloading Transcript..." : "Download Transcript",
      Icon: isTranscriptDownloading ? ArrowPathIcon : ArrowDownTrayIcon,
      callback: () => {
        if (media && media.videoSession?.id && !isTranscriptDownloading) {
          downloadTranscriptCSV(media.videoSession?.id);
        }
      },
    },
    {
      label: "Delete",
      Icon: TrashIcon,
      callback: () => {
        setIsDeleteMediaDialogOpen(true);
      },
    },
  ];
  return (
    <div className="flex flex-col w-full gap-1">
      <div className="flex flex-row justify-between items-center">
        <Paragraph size="medium" colorType="primary" className="ph-no-capture">
          {learnerFullName}
        </Paragraph>
        <div className="flex flex-row items-center gap-1">
          {roles.includes("bcba") && !isMissingMedia ? (
            <FeedTooltip text={media?.isViewed ? "Video is reviewed" : "Mark video as reviewed"}>
              <Button
                text={<CheckIcon className={`h-4 w-5 ${media?.isViewed ? "text-white" : "text-limestone-600"}`} />}
                appearance={media?.isViewed ? "primary" : "secondary"}
                onClick={(e) => {
                  e?.stopPropagation();
                  e?.preventDefault();
                  if (media) {
                    handleMarkAsReviewed(!media.isViewed);
                  }
                  return;
                }}
                disabled={isPending}
                className={clsx(
                  "relative items-center rounded-[4px]! px-1! py-1! max-h-6! min-w-6! text-text-primary! text-sm! font-normal! !hover:bg-limestone-50 pb-1!",
                  media && !media.isViewed && "ring-gray-200! bg-gray-50!",
                )}
              />
            </FeedTooltip>
          ) : null}
          {(roles.includes("bcba") || roles.includes("admin")) && !isMissingMedia ? (
            <PopoverMenu
              menuItems={
                roles.includes("admin")
                  ? menuItems.filter((item) => item.Icon !== CheckIcon)
                  : roles.includes("bcba") &&
                      media?.videoSession?.sessionStatus === ApiVideoSessionSessionStatusChoices.Completed
                    ? menuItems
                    : menuItems.filter((item) => item.Icon !== TrashIcon)
              }
              openDirection="to-bottom-left"
            />
          ) : null}
        </div>
      </div>
      <div className="flex flex-row justify-between">
        <Label size="extra-small">{date}</Label>
        <div className="flex flex-row gap-2 items-center">
          <FeedTooltip text={`${providerFirstName || "N/A"} ${providerLastName || ""}`}>
            <UserInitialsView
              className="h-5! w-5! text-[8px]! leading-4 gap-2 "
              firstName={providerFirstName || ""}
              lastName={providerLastName || ""}
            />
          </FeedTooltip>

          {sessionRating && (
            <div className="flex flex-row items-center gap-2">
              <span className="w-1 h-1 bg-gray-500 rounded-full"></span>
              <FeedTooltip text="Ratings are provided by your RBT after each client session.">
                <div className="flex flex-row gap-0.5 items-center">
                  <StarIcon className="text-yellow-500 w-3 h-3" color="grey" />
                  <Label size="extra-small">{sessionRating}</Label>
                </div>
              </FeedTooltip>
            </div>
          )}
        </div>
      </div>
      <Dialog
        title="Delete this video?"
        overflowBehavior="scroll"
        open={isDeleteMediaDialogOpen}
        setOpen={setIsDeleteMediaDialogOpen}
        primaryButton={{
          buttonAction: "destructive",
          text: (
            <div className="flex flex-row gap-1 items-center">
              <Paragraph className="text-white">Yes, Delete</Paragraph>
              {isMediaDeleting ? <div className="animate-spin border-b-2 rounded-full h-4 w-4 border-white" /> : null}
            </div>
          ),
          onClick: () => handleDeleteMedia(),
          disabled: isMediaDeleting,
        }}
        secondaryButton={{ text: "No, Cancel", onClick: () => setIsDeleteMediaDialogOpen(false) }}
      >
        <Paragraph>
          Deleting this video will permanently remove it along with any associated comments or feedback. This action
          cannot be undone.
        </Paragraph>
      </Dialog>
    </div>
  );
};
